/**
 * Reminder utility functions for MyLegacyBridge Family Reminders & Tribute Cards.
 */
import axios from 'axios';
import { getApiBase } from './apiBase';

const API = getApiBase();

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

export const remindersApi = {
  list: (treeId) => {
    const params = treeId ? `?tree_id=${treeId}` : '';
    return apiFetch(`${API}/reminders-api${params}`).then(normalizeReminderList);
  },
  upcoming: (days = 30) =>
    apiFetch(`${API}/reminders-api/upcoming?days=${days}`).then(normalizeReminderList),
  suggest: (personId) =>
    apiFetch(`${API}/reminders-api/suggest/${personId}`),
  create: (data) =>
    apiFetch(`${API}/reminders-api`, { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`${API}/reminders-api/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) =>
    apiFetch(`${API}/reminders-api/${id}`, { method: 'DELETE' }),
  notifications: {
    list: (limit = 30) => apiFetch(`${API}/notifications?limit=${limit}`),
    unreadCount: () => apiFetch(`${API}/notifications/unread-count`),
    markRead: (id) => apiFetch(`${API}/notifications/${id}/read`, { method: 'PATCH' }),
    markAllRead: () => apiFetch(`${API}/notifications/read-all`, { method: 'POST' }),
  },
};

async function apiFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const config = { url, method };
  if (options.body) {
    config.data = JSON.parse(options.body);
  }
  const response = await axios.request(config);
  return response.data;
}

/** Coerce reminder list payloads into a stable array shape. */
export function normalizeReminderList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.reminders)) return payload.reminders;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const REMINDER_TYPE_LABELS = {
  birthday: 'Birthday',
  death_anniversary: 'Remembrance Day',
  remembrance_day: 'Remembrance Day',
  wedding_anniversary: 'Anniversary',
  custom: 'Custom Reminder',
};

export const REMINDER_TYPE_ICONS = {
  birthday: '🎂',
  death_anniversary: '🕯️',
  remembrance_day: '🕯️',
  wedding_anniversary: '💛',
  custom: '📅',
};

export const REMINDER_TIMING_LABELS = {
  same_day: 'On the day',
  one_day_before: '1 day before',
  one_week_before: '1 week before',
};

export const REMINDER_REPEAT_LABELS = {
  yearly: 'Every year',
  once: 'Once only',
};

export function formatReminderDate(month, day, year) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const m = months[month - 1] || '';
  const y = year ? ` ${year}` : '';
  return `${m} ${day}${y}`;
}

export function daysUntilCopy(days) {
  if (days === null || days === undefined) return '';
  if (days < 0) return 'Passed';
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

export function daysUntilColor(days) {
  if (days === null || days === undefined) return 'text-muted-foreground';
  if (days < 0) return 'text-muted-foreground/60';
  if (days === 0) return 'text-primary font-semibold';
  if (days <= 3) return 'text-amber-600';
  if (days <= 7) return 'text-amber-500';
  return 'text-muted-foreground';
}

// ---------------------------------------------------------------------------
// Share helpers
// ---------------------------------------------------------------------------

export function makeShareMessage(reminder) {
  const name = reminder.person_name || 'someone special';
  const type = REMINDER_TYPE_LABELS[reminder.reminder_type] || 'special day';
  const date = formatReminderDate(reminder.date_month, reminder.date_day, reminder.date_year);
  const msg = reminder.custom_message || defaultCardMessage(reminder);
  return `${type} — ${name} · ${date}\n\n${msg}\n\nMyLegacyBridge`;
}

export function makeWhatsAppUrl(reminder) {
  const text = makeShareMessage(reminder);
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function defaultCardMessage(reminder) {
  const name = reminder.person_name || 'them';
  switch (reminder.reminder_type) {
    case 'birthday':
      return `Happy Birthday, ${name}. Thinking of you today and celebrating the memories, love, and stories that connect us.`;
    case 'death_anniversary':
    case 'remembrance_day':
      return `Remembering ${name} today. Their story, love, and presence remain part of this family archive.`;
    case 'wedding_anniversary':
      return `Celebrating ${name} today. Wishing a joyful anniversary and many more shared memories.`;
    default:
      return reminder.title || 'A family moment to remember today.';
  }
}

// ---------------------------------------------------------------------------
// Paywall readiness (not enforced — structure only)
// ---------------------------------------------------------------------------
// FREE: basic in-app reminders
// PAID: email reminders, digital card export, multiple reminders
// Future: WhatsApp/SMS add-ons
// To enforce: check user.plan_type in route handlers before creating reminders.
